import { __assign, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useState } from "react";
import { CalendarContainer, ContentBody, ContentFooter, DatePickerContent, DatePickerTrigger, DatePickerWrapper, StyledCalendar, DayButton, MonthYearHeader, } from "./styles";
import { addMonths, compareAsc, eachDayOfInterval, endOfDay, endOfMonth, format, isAfter, isBefore, isSameDay, startOfDay, startOfMonth, subMonths, } from "date-fns";
import { Button } from "../Button";
import { fillDaysArray } from "../../utils/functions";
import { ptBR } from "date-fns/locale";
import { CalendarInputs, CalendarOptions, } from "./CalendarComponents";
import { Spinner } from "../Spinner";
import { useTheme } from "styled-components";
import { IconArrowDropDown, IconCalendarCheck } from "../icons";
import { createPortal } from "react-dom";
var firstAndLastItem = function (array) {
    return [array[0], array[array.length - 1]];
};
export var Calendar = forwardRef(function Calendar(_a, ref) {
    var _b = _a.hideOptions, hideOptions = _b === void 0 ? [] : _b, hideAllOptions = _a.hideAllOptions, onlyDate = _a.onlyDate, onSelectDate = _a.onSelectDate, defaultDate = _a.defaultDate, isLoading = _a.isLoading, disabled = _a.disabled, minDate = _a.minDate, maxDate = _a.maxDate, value = _a.value, containerStyles = _a.containerStyles, props = __rest(_a, ["hideOptions", "hideAllOptions", "onlyDate", "onSelectDate", "defaultDate", "isLoading", "disabled", "minDate", "maxDate", "value", "containerStyles"]);
    var theme = useTheme();
    var _c = useState(false), show = _c[0], setShow = _c[1];
    var toggleShow = function () {
        setSelectedDate(dateOnButton);
        setShow(function (state) { return !state; });
    };
    var triggerRef = useRef(null);
    var modalRef = useRef(null);
    var _d = useState({ top: "0px", left: "0px" }), position = _d[0], setPosition = _d[1];
    var _e = useState(defaultDate || {
        start: startOfDay(new Date()),
        end: endOfDay(new Date()),
    }), selectedDate = _e[0], setSelectedDate = _e[1];
    var _f = useState(defaultDate || {
        start: startOfDay(new Date()),
        end: endOfDay(new Date()),
    }), dateOnButton = _f[0], setDateOnButton = _f[1];
    var _g = useState(false), isSelecting = _g[0], setIsSelecting = _g[1];
    var _h = useState(null), dayHovered = _h[0], setDayHovered = _h[1];
    var _j = useState([]), hoveredDays = _j[0], setHoveredDays = _j[1];
    var _k = useState({
        start: startOfMonth(selectedDate.start),
        end: endOfMonth(selectedDate.start),
    }), monthToShow = _k[0], setMonthToShow = _k[1];
    var _l = useState([]), daysToShow = _l[0], setDaysToShow = _l[1];
    var _m = firstAndLastItem(hoveredDays), firstItem = _m[0], lastItem = _m[1];
    var firstDay = firstItem && compareAsc(firstItem, selectedDate.start) < 0
        ? firstItem
        : selectedDate.start;
    var lastDay = lastItem && compareAsc(lastItem, selectedDate.start) > 0
        ? lastItem
        : selectedDate.end;
    var onClickDay = function (day) {
        setHoveredDays([]);
        if (!isSelecting) {
            setIsSelecting(true);
            setSelectedDate({
                start: startOfDay(day),
                end: endOfDay(day),
            });
            return;
        }
        if (compareAsc(day, selectedDate.start) > 0) {
            setSelectedDate({
                start: selectedDate.start,
                end: endOfDay(day),
            });
        }
        else {
            setSelectedDate({
                start: startOfDay(day),
                end: selectedDate.end,
            });
        }
        setIsSelecting(false);
    };
    var handleSelectDate = function (date) {
        onSelectDate === null || onSelectDate === void 0 ? void 0 : onSelectDate(date);
        setDateOnButton(date);
        toggleShow();
    };
    useEffect(function () {
        var daysOfTheMonth = eachDayOfInterval(monthToShow);
        fillDaysArray(daysOfTheMonth);
        setDaysToShow(daysOfTheMonth.map(function (day) {
            return {
                value: day,
                day: day.getDate(),
                month: day.getMonth(),
                year: day.getFullYear(),
                weekday: day.getDay(),
            };
        }));
    }, [monthToShow]);
    useEffect(function () {
        if (!dayHovered) {
            return;
        }
        if (compareAsc(dayHovered, selectedDate.start) > 0) {
            setHoveredDays(eachDayOfInterval({ start: selectedDate.start, end: dayHovered }));
        }
        else {
            setHoveredDays(eachDayOfInterval({ start: dayHovered, end: selectedDate.end }));
        }
    }, [dayHovered]);
    useEffect(function () {
        if (show && modalRef.current && triggerRef.current) {
            var modalRect = modalRef.current.getBoundingClientRect();
            var triggerRect = triggerRef.current.getBoundingClientRect();
            var windowHeight = window.innerHeight;
            if (triggerRect.bottom + modalRect.height > windowHeight) {
                setPosition({
                    left: triggerRect.left + triggerRect.width + "px",
                    top: triggerRect.top - modalRect.height - 4 + "px",
                });
            }
            else {
                setPosition({
                    left: triggerRect.left + triggerRect.width + "px",
                    top: triggerRect.top + triggerRect.height + 4 + "px",
                });
            }
        }
        else {
            setPosition({
                top: "0px",
                left: "0px",
            });
        }
    }, [show]);
    useEffect(function () {
        if (value) {
            setDateOnButton(value);
            setSelectedDate(value);
        }
    }, [value]);
    var handleClickOutside = function (event) {
        if (modalRef.current &&
            triggerRef.current &&
            !modalRef.current.contains(event.target) &&
            !triggerRef.current.contains(event.target)) {
            setShow(false);
        }
    };
    useEffect(function () {
        window.addEventListener("click", handleClickOutside);
        return function () {
            window.removeEventListener("click", handleClickOutside);
        };
    }, []);
    useEffect(function () {
        setMonthToShow({
            start: startOfMonth(selectedDate.start),
            end: endOfMonth(selectedDate.start),
        });
    }, [selectedDate]);
    return (_jsxs(DatePickerWrapper, { style: containerStyles, ref: ref, children: [_jsxs(DatePickerTrigger, __assign({ type: "button", ref: triggerRef, onClick: function () {
                    toggleShow();
                }, disabled: disabled || isLoading }, props, { children: [format(dateOnButton.start, "dd/MM/yyyy".concat(onlyDate ? "" : "- HH:mm")), _jsx("span", { children: "a" }), dateOnButton.end
                        ? format(dateOnButton.end, "dd/MM/yyyy".concat(onlyDate ? "" : "- HH:mm"))
                        : "-", isLoading ? (_jsx(Spinner, { size: 20, color: theme.colors.neutral.light })) : (_jsx(IconCalendarCheck, {}))] })), createPortal(_jsxs(DatePickerContent, { ref: modalRef, "$show": show, top: position.top, left: position.left, children: [_jsxs(ContentBody, { children: [!hideAllOptions && (_jsx(CalendarOptions, { hideOptions: hideOptions, setSelectedDate: setSelectedDate, handleSelectDate: handleSelectDate })), _jsxs(CalendarContainer, { children: [_jsxs(MonthYearHeader, { children: [_jsx(IconArrowDropDown, { style: { transform: "rotate(90deg)" }, onClick: function () {
                                                    return setMonthToShow({
                                                        start: subMonths(monthToShow.start, 1),
                                                        end: subMonths(monthToShow.end, 1),
                                                    });
                                                } }), _jsxs("span", { children: [format(monthToShow.start, "MMMM", { locale: ptBR }), ", ", format(monthToShow.start, "yyyy")] }), _jsx(IconArrowDropDown, { style: { transform: "rotate(-90deg)" }, onClick: function () {
                                                    return setMonthToShow({
                                                        start: addMonths(monthToShow.start, 1),
                                                        end: addMonths(monthToShow.end, 1),
                                                    });
                                                } })] }), _jsxs("ul", { children: [_jsx("li", { children: "D" }), _jsx("li", { children: "S" }), _jsx("li", { children: "T" }), _jsx("li", { children: "Q" }), _jsx("li", { children: "Q" }), _jsx("li", { children: "S" }), _jsx("li", { children: "S" })] }), _jsx(StyledCalendar, { children: daysToShow.map(function (day) {
                                            return (_jsx(DayButton, { onClick: function () { return onClickDay(day.value); }, onMouseEnter: function () {
                                                    return isSelecting && setDayHovered(day.value);
                                                }, onMouseLeave: function () { return isSelecting && setDayHovered(null); }, "$isHovered": hoveredDays.some(function (hoveredDay) {
                                                    return hoveredDay && compareAsc(hoveredDay, day.value) === 0;
                                                }), "$isSelected": isAfter(endOfDay(day.value), selectedDate.start) &&
                                                    isBefore(day.value, selectedDate.end), "$isFirst": isSameDay(firstDay, day.value), "$isLast": isSameDay(lastDay, day.value), disabled: (minDate && isBefore(day.value, minDate)) ||
                                                    (maxDate && isAfter(day.value, maxDate)), children: day.day }, day.value.toString()));
                                        }) }), !onlyDate && (_jsx(CalendarInputs, { selectedDate: selectedDate, setSelectedDate: setSelectedDate, setHoveredDays: setHoveredDays }))] })] }), _jsx(ContentFooter, { children: _jsx(Button, { variant: "outlined", onClick: function () {
                                handleSelectDate(selectedDate);
                            }, children: "Aplicar" }) })] }), document.body)] }));
});
//# sourceMappingURL=index.js.map